import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useTable, useSortBy } from "react-table";
import { useSticky } from "react-table-sticky";
import { motion } from "framer-motion";
import debounce from "lodash.debounce";

import ReactTooltip from "react-tooltip";
import {
  fetchSupporters,
  setTableError,
  setTableLoadingMore,
  setTableLoading,
  setTable404,
} from "../../actions/supporters";
import reportTypes from "./reportTypes";
import TableRow from "./TableRow";
import { TableLoader } from "./TableLoader";
import SupporterTableFilters from "./SupporterTableFilters";
import FinancialTableFooter from "./FinancialTableFooter";
import ContactDetailsModal from "./ContactDetailsModal";
import getSupporterTableColumns from "./SupporterTableColumns";
import ColumnHead from "./ColumnHead";
import fadeInUp from "../../helpers/Animations";
import SupportChangesModal from "./SupportChangesModal";

import "../../styles/components/_table.scss";

const receivedSupportPaginationLimit = 15;
const paginationLimit = 15;

/**
 * Merge records on the received support report so there is only one row per contact ID
 */
const mergeHistoryRecords = (data) => {
  const mergedRecords = {};
  data.forEach((record) => {
    const { id, fullname, lastname, status, ...months } = record;

    const mergedMonths = { ...months };

    if (mergedRecords[id]) {
      Object.entries(mergedMonths).forEach(([month, value]) => {
        mergedMonths[month] += value;
      });
    }

    mergedRecords[id] = { id, fullname, lastname, status, ...mergedMonths };
  });

  return Object.values(mergedRecords);
};

/*
 * Displays data for of the current programme's supporters
 */

const SupportersTable = ({
  error,
  setError,
  is404,
  setIs404,
  loading,
  setLoading,
  loadingMore,
  setLoadingMore,
  loadSupporters,
  programmeID,
  reportType,
  searchQuery,
}) => {
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(0);
  const skipPageResetRef = useRef();
  const [data, setData] = useState([]);
  const [received, setReceived] = useState([]);
  const [receivedProgramId, setReceivedProgramId] = useState(null);
  const [manualSortBy, setManualSortBy] = useState(true);
  const [prevProgrammeID, setPrevProgrammeID] = useState(undefined);
  const [prevSort, setPrevSort] = useState([]);

  const columns = useMemo(
    () => getSupporterTableColumns(reportType),
    [reportType]
  );

  const {
    headerGroups,
    rows,
    prepareRow,
    state: { sortBy },
  } = useTable(
    { columns, data, autoResetSortBy: !skipPageResetRef.current, manualSortBy },
    useSortBy,
    useSticky
  );

  const tableRef = useRef();

  const handleReceivedSupportSearch = useCallback(
    (query) =>
      received.filter((row) =>
        row.fullname.toLowerCase().includes(query.toLowerCase())
      ),
    [received]
  );

  useEffect(() => {
    ReactTooltip.rebuild();
  }, []);

  const fetchReceivedPagination = useCallback(
    (query, fetchedData, nextPage) => {
      setLoading(true);
      setLoadingMore(true);
      setIs404(false);
      setError("");

      loadSupporters(query.reportType, query.programmeID, query.sortBy, "", nextPage)
        .then(({ data: newData, meta }) => {
          skipPageResetRef.current = true;

          if (meta?.current_page <= meta.last_page) {
            const mergedRecords = mergeHistoryRecords(fetchedData.concat(newData));
            setReceived((prev) => prev.concat(newData));
            setData(mergedRecords);
            fetchReceivedPagination(query, fetchedData.concat(newData), nextPage + 1);
          } else {
            setLoading(false);
            setLoadingMore(false);
          }
        });
    },
    [loadSupporters, setError, setIs404, setLoadingMore, setLoading]
  );

  const handleReportFilterChange = useCallback(
    async ({ report, search, sort = [] }) => {
      setLoading(true);
      setData([]);
      setPage(1);
      setLastPage(0);
      const searchValue = search;
      const selectedReport = report || reportType;
      const isReceivedSupportReport = selectedReport === reportTypes.received;

      const manualSort =
        selectedReport !== reportTypes.received &&
        !(sort[0] && sort[0].id === "status");

      setManualSortBy(manualSort);

      // handle Received Support sorting without making call to api
      if (
        !manualSort &&
        received.length > 0 &&
        receivedProgramId === programmeID
      ) {
        if (searchValue) {
          // handle received support search without api call...
          const filtered = handleReceivedSupportSearch(
            searchValue,
            programmeID
          );
          setData(filtered);
        } else {
          setData(received);
        }
        setLoading(false);
        return;
      }

      setIs404(false);

      loadSupporters(selectedReport, programmeID, sort, searchValue).then(
        ({ data: newData, meta }) => {
          skipPageResetRef.current = true;
          if (newData) {
            if (isReceivedSupportReport) {
              const mergedRecords = mergeHistoryRecords(newData);
              setData(mergedRecords);
              setReceivedProgramId(programmeID);
              setReceived(newData);
              fetchReceivedPagination(
                { reportType: selectedReport, programmeID, sortBy: sort },
                newData,
                2
              );
            } else {
              setData(newData);
              setLastPage(meta.last_page);
            }
          }
        }
      );
    },
    [
      programmeID,
      fetchReceivedPagination,
      handleReceivedSupportSearch,
      loadSupporters,
      received,
      receivedProgramId,
      setIs404,
      setLoading,
      reportType,
      setData,
      setReceived,
      setReceivedProgramId,
    ]
  );

  useEffect(() => {
    // handle sort change..
    setPage(1);
    setLastPage(0);
    if (
      sortBy[0]?.id !== prevSort[0]?.id ||
      sortBy[0]?.desc !== prevSort[0]?.desc
    ) {
      handleReportFilterChange({
        report: reportType || reportTypes.regular,
        search: "",
        sort: sortBy,
      });
      setPrevSort(sortBy);
    }
  }, [sortBy, handleReportFilterChange, prevSort, setLoading, reportType]);

  useEffect(() => {
    // fetch initial view..
    if (programmeID && prevProgrammeID !== programmeID) {
      handleReportFilterChange({
        report: reportType || reportTypes.regular,
        search: "",
      });
      setPrevProgrammeID(programmeID);
    }
  }, [
    programmeID,
    handleReportFilterChange,
    prevProgrammeID,
    setLoading,
    reportType,
  ]);

  useEffect(() => {
    setReceived([]);
  }, [reportType, programmeID]);

  const handlePagination = useCallback(
    (nextPage) => {
      setLoadingMore(true);

      loadSupporters(
        reportType,
        programmeID,
        sortBy,
        searchQuery,
        nextPage
      ).then(({ data: newData }) => {
        skipPageResetRef.current = true;
        if (newData) {
          setData((prev) => prev.concat(newData));
        }
      });
    },
    [
      reportType,
      programmeID,
      sortBy,
      searchQuery,
      loadSupporters,
      setLoadingMore,
    ]
  );

  useEffect(() => {
    if (page > 1 && page <= lastPage && lastPage !== 0) {
      handlePagination(page);
    }
  }, [page, handlePagination, lastPage]);

  const handleScroll = useCallback(
    (el, parent = undefined) => {
      const atBottom = parent
        ? parent.innerHeight + el.scrollTop > el.offsetHeight - 250
        : el.clientHeight + el.scrollTop > el.offsetHeight - 250;

      if (
        !is404 &&
        !loadingMore &&
        atBottom &&
        reportType !== reportTypes.received
      ) {
        setIs404(false);
        setError("");
        setPage((prev) => prev + 1);
      }
    },
    [is404, loadingMore, setIs404, setError, reportType]
  );

  const onScroll = debounce(handleScroll, 500);

  window.onscroll = () =>
    reportType !== reportTypes.received
      ? onScroll(document.documentElement, window)
      : null;

  useEffect(() => {
    const table = tableRef?.current;

    const handleScroll = () => onScroll(table);

    if (table) {
      if (reportType !== reportTypes.received) {
        table.addEventListener("scroll", handleScroll);
      } else {
        table.removeEventListener("scroll", handleScroll);
      }
    }
    return () =>
      table ? table.removeEventListener("scroll", handleScroll) : null;
  }, [tableRef, reportType, onScroll]);

  if (!programmeID) {
    return null;
  }

  return columns.length ? (
    <motion.div animate={fadeInUp.animate} transition={fadeInUp.transition}>
      <SupporterTableFilters onReportFilterChange={handleReportFilterChange} />
      <br />
      <h2>{reportType}</h2>
      {reportType === reportTypes.regular && (
        <p>
          The Regular Support report reflects all current financial partnerships
          that you should expect to receive. This report does not include
          one-time gifts.
        </p>
      )}
      {reportType === reportTypes.received && (
        <p>
          The Received Support report (pivot table) is a historic view of all
          financial support you have received in the last two years.
        </p>
      )}
      {reportType === reportTypes.changes && (
        <p>
          The Financial Partner Changes report reflects any changes that a
          supporter has made to their financial support.
        </p>
      )}
      <motion.div
        animate={fadeInUp.animate}
        transition={fadeInUp.transition}
        className={`table sticky ${
          reportType === reportTypes.received && "table--received-support"
        } ${(loading || loadingMore) && "table--loading"}`}
        role="table"
        ref={tableRef}
      >
        <div className="table__wrap">
          <div className="table__body" role="rowgroup">
            {headerGroups.map((headerGroup, headerGroupIndex) => {
              const headerGroupProps = headerGroup.getHeaderGroupProps();
              return (
                <div
                  className="table__header"
                  key={headerGroupProps.key}
                  role={headerGroupProps.role}
                >
                  {headerGroup.headers.map((column) => {
                    const cellProps = column.getHeaderProps(
                      column.getSortByToggleProps()
                    );
                    // If this is a placeholder column, get the actual column and remove then replace it with the placeholder...
                    if (headerGroups.length === 2) {
                      if (column.depth === undefined) {
                        // eslint-disable-next-line no-param-reassign, prefer-destructuring
                        column = column.headers[0];
                      }
                      if (headerGroupIndex === 1) {
                        if (column.depth === 0) {
                          return (
                            <div
                              className="table__heading"
                              key={column.id}
                              data-sticky-last-left-td={
                                cellProps["data-sticky-last-left-td"] !==
                                undefined
                                  ? cellProps["data-sticky-last-left-td"]
                                  : null
                              }
                              data-sticky-td={
                                cellProps["data-sticky-td"] !== undefined
                                  ? cellProps["data-sticky-td"]
                                  : null
                              }
                              style={cellProps.style}
                            />
                          );
                        }
                      }
                    }
                    return (
                      <ColumnHead
                        column={column}
                        key={column.id}
                        loading={loading || loadingMore}
                        reportType={reportType}
                      />
                    );
                  })}
                </div>
              );
            })}
            {rows.length > 0 &&
              !loading &&
              rows.map((row, index) => {
                prepareRow(row);
                return (
                  <TableRow
                    row={row}
                    key={`${row.id}`}
                    columns={columns}
                    count={index + 1}
                  />
                );
              })}
            {(loading || loadingMore) && (
              <TableLoader columns={columns} reportType={reportType} />
            )}
            {reportType === reportTypes.received && !error && (
              <FinancialTableFooter
                columns={columns}
                rowData={data}
                loading={loading || loadingMore}
              />
            )}
          </div>
          {error && !(loading || loadingMore) && (
            <div className="programme-details__wrapper">
              <div className="programme-details__field">
                <p>{error}</p>
              </div>
            </div>
          )}
        </div>
      </motion.div>
      <ContactDetailsModal />
      <SupportChangesModal />
    </motion.div>
  ) : null;
};

SupportersTable.defaultProps = {
  reportType: "All",
  programmeID: null,
  searchQuery: "",
  loading: false,
  loadingMore: false,
  error: "",
  is404: false,
};

SupportersTable.propTypes = {
  reportType: PropTypes.string,
  programmeID: PropTypes.number,
  searchQuery: PropTypes.string,
  error: PropTypes.string,
  setError: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  is404: PropTypes.bool,
  loadingMore: PropTypes.bool,
  setLoading: PropTypes.func.isRequired,
  setLoadingMore: PropTypes.func.isRequired,
  setIs404: PropTypes.func.isRequired,
  loadSupporters: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
  setLoading: (loading) => dispatch(setTableLoading(loading)),
  setLoadingMore: (loadingMore) => dispatch(setTableLoadingMore(loadingMore)),
  setIs404: (is404) => dispatch(setTable404(is404)),
  setError: (error) => dispatch(setTableError(error)),
  loadSupporters: (
    storeKey,
    reportType,
    programmeID,
    sortBy,
    searchQuery,
    page
  ) =>
    dispatch(
      fetchSupporters(
        storeKey,
        reportType,
        programmeID,
        sortBy,
        searchQuery,
        page
      )
    ),
});

const mapStateToProps = (state) => {
  return {
    reportType: state.reports.type,
    programmeID: state.reports.programme.id,
    searchQuery: state.reports.table.query,
    loading: state.reports.table.loading,
    loadingMore: state.reports.table.loadingMore,
    is404: state.reports.table.is404,
    error: state.reports.table.error,
    reports: state.session.userdata.reports,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SupportersTable);
